import type { SimpleChanges } from '@angular/core';
import {
  ChangeDetectorRef,
  Component,
  inject,
  input,
  ChangeDetectionStrategy,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { Store } from '@ngrx/store';

import { I18nService } from '@core/i18n-legacy';
import type { RequisitionAvailableFlow } from '@core/requisitions-types';
import { RequisitionsActions } from '@recruitee/requisitions-data';

import { RequisitionsFlowSelectComponent } from '../../../components/requisitions-flow-select/requisitions-flow-select.component';
import { ApprovalFlowFacade } from '../../../facades/single-requisition/approval-flow.facade';
import type { FlowSelectOption } from '../../../requisitions.types';

@Component({
  selector: 'rt-requisitions-flow-select-container',
  template: `
    @if (isSelectFlowVisible) {
      <rt-requisitions-flow-select
        [isDisabled]="isSelectFlowDisabled"
        [options]="options"
        [selectedOption]="selectedOption"
        (select)="setFlow($event)"
      />
    }
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [RequisitionsFlowSelectComponent],
})
export class RequisitionsFlowSelectContainerComponent {
  private readonly store = inject(Store);
  private readonly cdRef = inject(ChangeDetectorRef);
  private readonly i18n = inject(I18nService);
  private readonly route = inject(ActivatedRoute);
  private readonly approvalFlowFacade = inject(ApprovalFlowFacade);

  private readonly customFlowLabel = this.i18n.translate('app.requisitions.flow.custom_flow');

  public readonly requisitionId = input<number>(undefined!);
  public readonly assigned = input<RequisitionAvailableFlow>();
  public readonly flows = input<RequisitionAvailableFlow[]>([]);

  public isSelectFlowVisible = false;
  public isSelectFlowDisabled = false;
  public selectedOption?: FlowSelectOption;
  public options: FlowSelectOption[] = [];

  public ngOnChanges(changes: SimpleChanges) {
    if (changes.flows?.currentValue) {
      this.options = this.getFlowOptions(this.flows());
      this.setSelectedOption();
    }
    if (changes.assigned) {
      this.setSelectedOption();
    }

    if (changes.requisitionId?.currentValue) {
      this.isSelectFlowDisabled = !this.approvalFlowFacade.canChangeFlow;
    }

    this.isSelectFlowVisible =
      this.approvalFlowFacade.isFlowEditable &&
      (this.assigned() ? this.options.length > 1 : this.options.length > 0);
  }

  public setFlow(option: FlowSelectOption) {
    this.store.dispatch(
      RequisitionsActions.setFlow({
        payload: {
          requisitionId: this.requisitionId(),
          flow: { id: option.value, title: option.label },
        },
      }),
    );
  }

  private setSelectedOption() {
    const assigned = this.assigned();
    this.selectedOption = assigned
      ? {
          value: assigned.id,
          label: assigned.custom ? this.customFlowLabel : assigned.title,
        }
      : undefined;
  }

  private getFlowOptions(flows: RequisitionAvailableFlow[]): FlowSelectOption[] {
    return flows.map(flow => ({
      value: flow.id!,
      label: flow.custom ? this.i18n.translate('app.requisitions.flow.custom_flow') : flow.title,
    }));
  }
}
